import { useState, useEffect, useCallback } from "react";
import {
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { OrgQCTemplatesRef, AdminsRef } from "../../../firebase";

/**
 * QuickConnect (QuickCollect) guest-page templates.
 *
 * Collection: `orgQCTemplates`
 * Fields: name, greeting (HTML), organization, createdAt/By, updatedAt/By
 *
 * There is one shared seed document with id `default` and `organization: ""`.
 * Orgs without their own template inherit it read-only; saving an edit creates
 * a new org-scoped document instead of mutating the shared default.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */

export const QC_DEFAULT_TEMPLATE_ID = "default";

// Attach admin user data so the table can show "Updated By".
const withAdminData = async (templateData) => {
  const result = { ...templateData };

  for (const field of ["updatedBy", "createdBy"]) {
    if (!result[field]) continue;
    try {
      const userDoc = await getDoc(doc(AdminsRef, result[field]));
      if (userDoc.exists()) {
        result[`${field}Data`] = userDoc.data();
      }
    } catch (e) {
      console.log(`Could not fetch ${field} user:`, e);
    }
  }

  return result;
};

/**
 * Fetch the QC templates for an organization.
 * Falls back to the shared `default` document when the org has none of its own,
 * flagged with `isDefault: true` so the UI can label it.
 */
export const useGetQCTemplates = (organizationId) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const q = query(
        OrgQCTemplatesRef,
        where("organization", "==", organizationId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const rows = [];
        for (const docSnapshot of querySnapshot.docs) {
          rows.push(
            await withAdminData({
              id: docSnapshot.id,
              ...docSnapshot.data(),
              isDefault: false,
            })
          );
        }
        setTemplates(rows);
        setError(null);
        return;
      }

      // No org-specific template yet: inherit the shared default.
      const defaultSnap = await getDoc(
        doc(OrgQCTemplatesRef, QC_DEFAULT_TEMPLATE_ID)
      );

      if (defaultSnap.exists()) {
        setTemplates([
          await withAdminData({
            id: defaultSnap.id,
            ...defaultSnap.data(),
            isDefault: true,
          }),
        ]);
      } else {
        setTemplates([]);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching QC templates:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refetch: fetchTemplates };
};

/** Fetch a single QC template by id. Works for the shared `default` doc too. */
export const useGetQCTemplateById = (templateId) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplate = useCallback(async () => {
    if (!templateId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const templateDoc = await getDoc(doc(OrgQCTemplatesRef, templateId));

      if (templateDoc.exists()) {
        const data = templateDoc.data();
        setTemplate(
          await withAdminData({
            id: templateDoc.id,
            ...data,
            // The shared seed doc has no organization.
            isDefault:
              templateDoc.id === QC_DEFAULT_TEMPLATE_ID || !data.organization,
          })
        );
        setError(null);
      } else {
        setError("Template not found");
      }
    } catch (err) {
      console.error("Error fetching QC template:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { template, loading, error, refetch: fetchTemplate };
};

/**
 * Save a QC template.
 *
 * Editing the inherited default creates a NEW org-scoped document; the shared
 * `default` doc is never mutated by an org admin. Otherwise updates in place.
 */
export const useSaveQCTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const saveTemplate = async ({
    templateId,
    isDefault,
    formData,
    userId,
    organizationId,
    // General Admin owns the shared `default` doc and edits it in place.
    // Org admins never get this flag, so they always fork instead.
    editInPlace = false,
  }) => {
    try {
      setLoading(true);
      setError(null);

      const createNew =
        !editInPlace && (isDefault || templateId === QC_DEFAULT_TEMPLATE_ID);

      if (createNew) {
        if (!organizationId) {
          throw new Error("Missing organization.");
        }

        const docRef = await addDoc(OrgQCTemplatesRef, {
          organization: organizationId,
          name: formData.name || "",
          greeting: formData.greeting || "",
          createdBy: userId || "",
          createdAt: serverTimestamp(),
          updatedBy: userId || "",
          updatedAt: serverTimestamp(),
        });

        return { success: true, id: docRef.id, created: true };
      }

      await updateDoc(doc(OrgQCTemplatesRef, templateId), {
        name: formData.name || "",
        greeting: formData.greeting || "",
        updatedBy: userId || "",
        updatedAt: serverTimestamp(),
      });

      return { success: true, id: templateId, created: false };
    } catch (err) {
      console.error("Error saving QC template:", err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return { saveTemplate, loading, error };
};